import React, { useMemo, useCallback } from "react";
import {
  Box,
  Flex,
  HStack,
  Text,
  Skeleton,
  Divider,
  Image,
  Tooltip as ChakraTooltip,
  VStack,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";

import { attendanceSummaryCall } from "../../api/attendanceSummary";
import calendarIcon from "../../assets/images/onleave/calendar.svg";

// ---------- Helpers ----------
const fmtInt = (n) =>
  Number.isFinite(Number(n)) ? Number(n).toLocaleString("vi-VN") : "0";

const fmtPct = (n) =>
  Number.isFinite(Number(n)) ? `${Number(n).toFixed(2)}%` : "0.00%";

const pickResponseData = (res) => res || null;

// Tooltip cho Recharts
const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="8px"
      px="10px"
      py="8px"
      boxShadow="sm"
    >
      <Text fontSize="13px" fontWeight={700} color="var(--color-info-employee)">
        {d.label}
      </Text>
      <Text fontSize="12px" color="#51607B">
        {fmtInt(d.value)} ({fmtPct(d.pct)})
      </Text>
    </Box>
  );
};

/**
 * onPickItem signature gợi ý:
 * onPickItem({ type: "A"|"B"|"C"|"D"|"E"|"ALL", label, value, pct })
 */
const AttendanceSummaryChart = ({
  params,
  title = "Tổng quan chuyên cần",
  onPickItem,
}) => {
  const queryKey = useMemo(
    () => ["attendance-summary-chart", params || {}],
    [params],
  );

  const fetcher = useCallback(
    () => attendanceSummaryCall.chart(params),
    [params],
  );

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey,
    queryFn: fetcher,
    placeholderData: (prev) => prev,
  });

  const payload = useMemo(() => pickResponseData(data), [data]);

  const items = useMemo(() => {
    const rates = payload?.rates || {};
    return [
      {
        key: "A",
        type: "A",
        label: "Đi làm",
        value: payload?.A_checkedIn || 0,
        pct: rates?.A_checkedInPct ?? 0,
        color: "#2B6CB0",
      },
      {
        key: "B",
        type: "B",
        label: "Vắng",
        value: payload?.B_absent || 0,
        pct: rates?.B_absentPct ?? 0,
        color: "#90CDF4",
      },
      {
        key: "C",
        type: "C",
        label: "Chấm công ngoài",
        value: payload?.C_registerOutside || 0,
        pct: rates?.C_registerOutsidePct ?? 0,
        color: "#CBD5E0",
      },
      {
        key: "D",
        type: "D",
        label: "Nghỉ phép",
        value: payload?.D_leave || 0,
        pct: rates?.D_leavePct ?? 0,
        color: "#E2E8F0",
      },
      {
        key: "E",
        type: "E",
        label: "Làm bù",
        value: payload?.E_makeupWork || 0,
        pct: rates?.E_makeupWorkPct ?? 0,
        color: "#A0AEC0",
      },
    ].filter((x) => x.key !== "E" || payload?.E_makeupWork !== undefined);
  }, [payload]);

  const rangeText = useMemo(() => {
    const s = payload?.range?.start;
    const e = payload?.range?.end;
    if (!s && !e) return "";
    if (s && e) return `${s} đến ${e}`;
    return s || e;
  }, [payload]);

  const handlePick = useCallback(
    (it) => {
      if (typeof onPickItem === "function") onPickItem(it);
    },
    [onPickItem],
  );

  const totalEmployees = payload?.totalEmployees || 0;
  const totalRecords = payload?.totalRecords || 0;

  return (
    <Flex
      backgroundColor="var(--color-main)"
      borderRadius={{ base: "12px" }}
      flexDirection="column"
      p="16px"
      w="50%"
      gap="12px"
    >
      <Flex align="center" justify="space-between">
        <Text
          fontSize="16px"
          fontWeight={700}
          color="var(--color-info-employee)"
        >
          {title}
        </Text>

        <Flex direction="column" align="flex-end" gap="2px">
          <Text fontSize="12px" color="#51607B">
            Tổng số NV: {fmtInt(totalEmployees)}
          </Text>

          <ChakraTooltip label="Tổng số bản ghi" hasArrow>
            <Text
              fontSize="12px"
              color="#51607B"
              fontWeight={700}
              cursor={onPickItem ? "pointer" : "default"}
              onClick={() =>
                handlePick({
                  key: "ALL",
                  type: "ALL",
                  label: "Tổng record",
                  value: totalRecords,
                  pct: 100,
                })
              }
            >
              Tổng: {fmtInt(totalRecords)}
            </Text>
          </ChakraTooltip>
        </Flex>
      </Flex>

      <Skeleton isLoaded={!isLoading && !isFetching}>
        <Box>
          <Box w="100%" h="220px">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={items}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={90}
                  paddingAngle={2}
                  stroke="transparent"
                  isAnimationActive={false}
                  onClick={(_, index) => {
                    const it = items?.[index];
                    if (it) handlePick(it);
                  }}
                >
                  {items.map((it) => (
                    <Cell key={it.key} fill={it.color} />
                  ))}
                </Pie>

                <RechartsTooltip
                  content={<ChartTooltip />}
                  wrapperStyle={{ outline: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>

          {/* Legend */}
          <VStack justify="center" spacing={2} mt="-6px" align="center">
            {items.map((it) => (
              <ChakraTooltip
                key={it.key}
                label={`${it.label}: ${fmtInt(it.value)} (${fmtPct(it.pct)})`}
                hasArrow
              >
                <HStack
                  spacing={2}
                  cursor={onPickItem ? "pointer" : "default"}
                  onClick={() => handlePick(it)}
                >
                  <Box w="10px" h="10px" borderRadius="full" bg={it.color} />
                  <Text fontSize="13px" color="#51607B" fontWeight={500}>
                    {it.label} {`(${fmtInt(it.value)} - ${fmtPct(it.pct)})`}
                  </Text>
                </HStack>
              </ChakraTooltip>
            ))}
          </VStack>

          {/* Date range */}
          {rangeText ? (
            <>
              <Divider my="12px" />
              <Flex align="center" justify="center" gap={2}>
                <Image src={calendarIcon} w="16px" h="16px" />
                <Text fontSize="13px" color="#51607B" fontWeight={500}>
                  {rangeText}
                </Text>
              </Flex>
            </>
          ) : null}

          {error ? (
            <Text mt="10px" fontSize="12px" color="red.500">
              Không tải được dữ liệu biểu đồ.
            </Text>
          ) : null}
        </Box>
      </Skeleton>
    </Flex>
  );
};

export default AttendanceSummaryChart;
